import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { VehicleGallery } from '../components/inventory/VehicleGallery';
import { VehicleInfoCard } from '../components/inventory/VehicleInfoCard';
import { CarfaxBadge } from '../components/inventory/CarfaxBadge';
import { VehicleContactPanel } from '../components/inventory/VehicleContactPanel';
import { QuickApplyPanel } from '../components/inventory/QuickApplyPanel';
import { fmtKm } from '../components/inventory/inventoryUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VehicleDetail = () => {
  const { id } = useParams();
  const { language } = useLanguage();
  const en = language === 'en';
  const [v, setV] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applyOpen, setApplyOpen] = useState(false);
  const sideRef = useRef(null);
  const openApply = () => {
    setApplyOpen(true);
    setTimeout(() => sideRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };
  const closeApply = useCallback(() => setApplyOpen(false), []);

  useEffect(() => {
    const h = () => {
      setApplyOpen(true);
      setTimeout(() => sideRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    };
    window.addEventListener('open-quick-apply', h);
    return () => window.removeEventListener('open-quick-apply', h);
  }, []);

  useEffect(() => {
    setLoading(true);
    setApplyOpen(false);
    axios.get(`${API}/vehicles/inventory/${id}`)
      .then((res) => { setV(res.data); document.title = `${res.data.year} ${res.data.make} ${res.data.model} ${res.data.trim || ''} | Credit Auto Reset`; })
      .catch(() => setV(null))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [id]);

  if (loading) {
    return <div className="min-h-screen pt-32 flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-[#1a5c3b]" /></div>;
  }
  if (!v) {
    return (
      <div className="min-h-screen pt-32 bg-slate-50 flex flex-col items-center justify-center" data-testid="vehicle-not-found">
        <p className="text-gray-500 text-lg mb-4">{en ? 'This vehicle is no longer available' : 'Ce véhicule n’est plus disponible'}</p>
        <Link to="/nos-vehicules" className="bg-[#1a5c3b] text-white px-6 py-3 rounded-full font-semibold">{en ? 'Back to inventory' : 'Retour à l’inventaire'}</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 pt-[112px] md:pt-[148px] pb-24 md:pb-0" data-testid="vehicle-detail-page">
      <div className="bg-slate-100 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3">
          <Link to="/nos-vehicules" className="inline-flex items-center text-sm text-slate-500 hover:text-[#1a5c3b] transition-colors" data-testid="back-to-inventory">
            <ChevronLeft className="h-4 w-4 mr-1" />{en ? 'Back to inventory' : 'Retour à l’inventaire'}
          </Link>
        </div>
      </div>
      <div className="bg-slate-900 text-white py-3 sm:py-4">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <p className="font-bold text-base sm:text-lg md:text-2xl uppercase">
            {v.year} {v.make} {v.model} {v.trim || ''}{v.title ? ` • ${v.title}` : ''} • {fmtKm(v.mileage, en)}
          </p>
        </div>
      </div>

      <div className="flex-1 py-8">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
            <div className="lg:col-span-8 min-w-0"><VehicleGallery vehicle={v} en={en} /></div>
            <div ref={sideRef} className={`lg:col-span-4 lg:col-start-9 lg:row-start-1 lg:row-span-5 scroll-mt-32 ${applyOpen ? '' : 'order-last lg:order-none'}`}>
              {applyOpen
                ? <QuickApplyPanel v={v} en={en} onClose={closeApply} />
                : <VehicleContactPanel v={v} en={en} onApply={openApply} />}
            </div>
            <div className="lg:col-span-8 min-w-0"><VehicleInfoCard v={v} en={en} onApply={openApply} /></div>
            {v.description && (
              <div className="lg:col-span-8 bg-white border border-gray-100 rounded-2xl p-6 shadow-sm" data-testid="vehicle-description">
                <h2 className="font-bold text-xl mb-3 text-gray-900">Description</h2>
                <p className="text-gray-600 leading-relaxed whitespace-pre-line">{v.description}</p>
              </div>
            )}
            {v.carfax_badge_img && (
              <div className="lg:col-span-8 bg-white border border-gray-100 rounded-2xl p-6 shadow-sm" data-testid="carfax-section">
                <h2 className="font-bold text-xl text-gray-900 mb-4">{en ? 'Vehicle history' : 'Historique du véhicule'}</h2>
                <div className="flex flex-wrap items-center gap-4">
                  <CarfaxBadge v={v} en={en} className="h-12" testid="carfax-badge-vdp" />
                  {v.carfax_report_url && (
                    <a href={(en && v.carfax_report_url_en) || v.carfax_report_url} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-[#1a5c3b] underline underline-offset-4 hover:text-[#0f3d26]" data-testid="carfax-report-link">
                      {en ? 'View the free CARFAX report' : 'Voir le rapport CARFAX gratuit'}
                    </a>
                  )}
                </div>
              </div>
            )}
            <p className="lg:col-span-8 text-xs text-slate-400">{en ? '*Prices exclude taxes and applicable fees.' : '*Les prix excluent les taxes et frais applicables.'}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleDetail;
